import JSZip from "jszip";
import api from "@/lib/api";
import { buildInvoicePDF, buildReceiptPDF, buildJournalPDF, buildStatementPDF, buildDayBookPDF, buildTablePDF } from "@/lib/pdf";

const INR = (n) => `₹ ${Number(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const NUM = (n) => Number(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});
const safe = (s) => String(s||"file").replace(/[\\/:*?"<>|]+/g, "-").trim().slice(0, 80);
const DOCS_PER_ZIP = 250;
const tick = () => new Promise(r => setTimeout(r, 0));

export function parseCSV(text) {
  const rows = []; let row = [], field = "", inQ = false;
  const src = text.replace(/^\uFEFF/, "");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQ) { if (ch === '"') { if (src[i+1] === '"') { field += '"'; i++; } else inQ = false; } else field += ch; }
    else if (ch === '"') inQ = true;
    else if (ch === ",") { row.push(field); field = ""; }
    else if (ch === "\n" || ch === "\r") { if (ch === "\r" && src[i+1] === "\n") i++; row.push(field); rows.push(row); row = []; field = ""; }
    else field += ch;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  const clean = rows.filter(r => r.some(c => String(c).trim() !== ""));
  if (!clean.length) return [];
  const header = clean[0].map(h => h.trim().toLowerCase().replace(/\s+/g, "_"));
  return clean.slice(1).map(r => Object.fromEntries(header.map((h, i) => [h, (r[i] ?? "").trim()])));
}

export const TEMPLATES = {
  customers: { file: "customers-template.csv", header: ["name","phone","email","address","city","state","gstin","opening_balance"], sample: ["Acme Traders","9876543210","acme@example.com","12 Market Road","Bengaluru","Karnataka","29ABCDE1234F1Z5","15000"] },
  products: { file: "products-template.csv", header: ["name","unit","rate","enable_multiplier_breakdown"], sample: ["Curtain Fabric","Meters","250","TRUE"] },
};

export function downloadText(filename, text, type = "text/csv") {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const rangeLabel = (r) => `${r.from || "start"}_to_${r.to || "today"}`;

export async function runMassExport({ range, include, onProgress }) {
  const params = {}; if (range.from) params.from_date = range.from; if (range.to) params.to_date = range.to;
  onProgress({ phase: "Fetching data…", done: 0, total: 1 });
  const { data: b } = await api.get("/export/bundle", { params });
  const cmap = Object.fromEntries(b.customers.map(c => [c.id, c]));
  const jobs = [];
  if (include.invoices) b.invoices.forEach(i => jobs.push({ folder: "Invoices", name: `${safe(i.invoice_number)}.pdf`, build: () => buildInvoicePDF(b.company, i, cmap[i.customer_id]) }));
  if (include.receipts) b.receipts.forEach(r => jobs.push({ folder: "Receipts", name: `${safe(r.receipt_number)}.pdf`, build: () => buildReceiptPDF(b.company, r, cmap[r.customer_id]) }));
  if (include.journals) b.journal_entries.forEach(j => jobs.push({ folder: "Journal Vouchers", name: `${safe(j.voucher_number)}.pdf`, build: () => buildJournalPDF(b.company, j, cmap[j.customer_id]) }));
  if (include.statements) b.statements.forEach(s => jobs.push({ folder: "Statements", name: `${safe(s.customer.name)}.pdf`, build: () => buildStatementPDF(b.company, s) }));
  if (include.reports) {
    const period = [["PERIOD", range.from || range.to ? `${range.from || "Start"} – ${range.to || "Today"}` : "All transactions"]];
    jobs.push({ folder: "Reports", name: "Customer Balances.pdf", build: () => buildTablePDF(b.company, "CUSTOMER BALANCES", [["AS ON", new Date().toLocaleDateString("en-IN")]], ["Customer","Phone","Balance"], b.customer_balance.map(c => [c.name, c.phone, INR(c.balance)]), ["","Total Outstanding", INR(b.customer_balance.reduce((s,c)=>s+c.balance,0))], [2]) });
    jobs.push({ folder: "Reports", name: "Day Book.pdf", build: () => buildDayBookPDF(b.company, b.daybook) });
    jobs.push({ folder: "Reports", name: "Product Sales.pdf", build: () => buildTablePDF(b.company, "PRODUCT SALES", period, ["Product","Unit","Qty","Revenue"], b.product_sales.map(p => [p.product_name, p.unit, NUM(p.qty), INR(p.revenue)]), ["","","Total", INR(b.product_sales.reduce((s,p)=>s+p.revenue,0))], [2,3]) });
    jobs.push({ folder: "Reports", name: "Sales by Customer.pdf", build: () => buildTablePDF(b.company, "SALES BY CUSTOMER", period, ["Customer","Product","Qty","Avg Rate","Revenue"], b.product_sales_by_customer.map(p => [p.customer_name, p.product_name, NUM(p.qty), INR(p.avg_rate), INR(p.revenue)]), ["","","","Total", INR(b.product_sales_by_customer.reduce((s,p)=>s+p.revenue,0))], [2,3,4]) });
  }
  let backupJson = null;
  if (include.backup) { const { data } = await api.get("/backup"); backupJson = JSON.stringify(data); }

  const total = jobs.length + (backupJson ? 1 : 0);
  const parts = Math.max(1, Math.ceil(jobs.length / DOCS_PER_ZIP));
  const stamp = new Date().toISOString().slice(0,10);
  let done = 0, failed = [];
  for (let p = 0; p < parts; p++) {
    const zip = new JSZip();
    const slice = jobs.slice(p * DOCS_PER_ZIP, (p + 1) * DOCS_PER_ZIP);
    for (const job of slice) {
      try { const doc = await job.build(); zip.file(`${job.folder}/${job.name}`, doc.output("arraybuffer")); }
      catch (e) { failed.push(`${job.folder}/${job.name}`); }
      done++;
      if (done % 5 === 0) { onProgress({ phase: `Generating PDFs (part ${p+1}/${parts})…`, done, total }); await tick(); }
    }
    if (p === parts - 1 && backupJson) { zip.file("Backup/ledgerfast-backup.json", backupJson); done++; }
    zip.file("README.txt", [`LedgerFast export — ${new Date().toLocaleString("en-IN")}`, `Period: ${range.from || "start"} to ${range.to || "today"}`, `Part ${p+1} of ${parts}`, `Documents in this part: ${slice.length}`, failed.length ? `Failed: ${failed.join(", ")}` : "All documents generated successfully.", "", "Folders: Invoices/, Receipts/, Journal Vouchers/, Statements/, Reports/, Backup/"].join("\n"));
    onProgress({ phase: `Compressing part ${p+1}/${parts}…`, done, total });
    const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `LedgerFast-Export-${stamp}-${rangeLabel(range)}${parts > 1 ? `-part${p+1}of${parts}` : ""}.zip`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    await tick();
  }
  onProgress({ phase: "Done", done: total, total });
  return { total, parts, failed };
}
